import { CostBand } from "@sauron/contracts"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"
import {
  ApiError,
  api,
  type HealthAction,
  type HealthStatus,
  type ProviderHealth,
  type ProviderTraffic,
} from "@/api.js"
import { type DeclaredMetric, DeclaredTable } from "@/components/Declared.js"
import { Notice } from "@/components/Page.js"
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion.js"
import { Badge } from "@/components/ui/badge.js"
import { Button } from "@/components/ui/button.js"
import { Input, Label } from "@/components/ui/input.js"
import { Block, FactGrid, Figures, Strip } from "./facts.js"

/**
 * One source, as a card that answers triage without being opened: the state in
 * a word, the sentence the source wrote about itself, and the one or two
 * figures its grade turns on. Its full report is behind **Szczegóły**.
 */

/** How badly a source is doing, for sorting. `unknown` means its report threw. */
export const SEVERITY: Record<HealthStatus, number> = {
  down: 0,
  unknown: 1,
  degraded: 2,
  ok: 3,
}

/**
 * The state as a badge: colour, glyph and word together. These screens get
 * screenshotted into tickets, where a coloured dot survives badly and a
 * greyscale one says nothing at all.
 */
const STATUS: Record<
  HealthStatus,
  { label: string; tone: "allow" | "prepay" | "critical" | "neutral"; glyph: string }
> = {
  ok: { label: "sprawne", tone: "allow", glyph: "✓" },
  degraded: { label: "słabnie", tone: "prepay", glyph: "!" },
  down: { label: "nie działa", tone: "critical", glyph: "×" },
  unknown: { label: "nie wiadomo", tone: "neutral", glyph: "?" },
}

export function StatusBadge({ status }: { status: HealthStatus }) {
  const spec = STATUS[status]
  return (
    <Badge tone={spec.tone} glyph={spec.glyph} title={status}>
      {spec.label}
    </Badge>
  )
}

export function statusLabel(status: HealthStatus): string {
  return STATUS[status].label
}

export function SourceCard({ provider }: { provider: ProviderHealth }) {
  const declared = provider.metrics.filter((metric) => metric.lead === true)
  /*
   * **Every source shows some of its own numbers on the card**, opened or not.
   * A source that declares no lead still has figures worth seeing, so the first
   * three stand in — the alternative is a card with a status and nothing to
   * read, which is what a source nobody has finished looks like.
   */
  const lead = declared.length > 0 ? declared : provider.metrics.slice(0, 3)
  const rest = provider.metrics.filter((metric) => !lead.includes(metric))
  const traffic = provider.traffic

  return (
    <AccordionItem value={provider.providerId}>
      <div className="flex flex-wrap items-start justify-between gap-x-10 gap-y-5 p-4">
        <div className="min-w-[16rem] max-w-md flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className="text-[15px] font-semibold text-foreground">{provider.title}</h3>
            <StatusBadge status={provider.status} />
          </div>
          <p className="text-[12.5px] text-muted-foreground">
            <span className="font-mono">{provider.providerId}</span>
            {" · "}
            {provider.countries === "all" ? "wszystkie kraje" : provider.countries.join(", ")}
            {" · "}
            {costLabel(provider.worstCaseCost)}
          </p>
          {/* The one sentence on the screen that says *why*. It is the source's
              own, and it is the reason the card is worth reading at all. */}
          <p className="text-sm text-foreground">{provider.summary}</p>
        </div>

        <Figures facts={[...localShare(traffic, provider.worstCaseCost), ...lead]} />
      </div>

      {traffic.failures > 0 && (
        <div className="px-4 pb-4">
          <Notice tone="critical">Nieudane wywołania: {traffic.failures}</Notice>
        </div>
      )}

      {/* Above the fold, not behind *Szczegóły*: a source declares an action
          only while it is the thing standing between here and working, so the
          fix belongs on the screen that reported the problem. */}
      {(provider.actions ?? []).map((action) => (
        <SourceAction key={action.key} providerId={provider.providerId} action={action} />
      ))}

      <div className="border-t px-2 py-1.5">
        <AccordionTrigger aria-label={`Szczegóły: ${provider.title}`}>Szczegóły</AccordionTrigger>
      </div>

      <AccordionContent>
        <div className="space-y-6 border-t px-4 py-4">
          {/* Counted by the core from the audit log, identically for every
              source. Kept apart from what the source says about itself, which
              is the one thing a source must not be allowed to mark itself on. */}
          <Block title="Ruch (24 h)">
            {traffic.calls === 0 ? (
              <p className="text-[13px] text-muted-foreground">Brak wywołań w tym oknie.</p>
            ) : (
              <Strip facts={trafficFacts(traffic, provider.worstCaseCost)} />
            )}
          </Block>

          {rest.length > 0 && (
            <Block title="Raport źródła">
              <FactGrid facts={rest} />
            </Block>
          )}

          {provider.tables.map((table) => (
            <DeclaredTable key={table.key} table={table} initialRows={5} />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

/**
 * An action the source declared, as one labelled field and a button.
 *
 * **Nothing here knows which source it is serving.** The heading, the sentence,
 * the field's label and the button all come from the report, so the source that
 * needs a person is the source that says so — the same arrangement as every
 * figure on this screen. Adding a second one is a declaration, not an edit.
 *
 * The value is treated as a credential throughout: a masked field, no
 * autocomplete, no spellcheck, cleared the moment it is accepted, and never
 * rendered back. These screens get screenshotted into tickets, and a token
 * legible in one is a token in a ticket.
 */
function SourceAction({ providerId, action }: { providerId: string; action: HealthAction }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState("")
  const fieldId = `action-${providerId}-${action.key}`

  const run = useMutation({
    mutationFn: (secret: string) => api.runProviderAction(providerId, action.key, secret),
    onSuccess: () => {
      setValue("")
      // The source stops declaring the action once it no longer needs one, so
      // refetching is what makes the control disappear rather than linger as a
      // button that would now do nothing.
      void queryClient.invalidateQueries({ queryKey: ["health"] })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    const secret = value.trim()
    if (secret.length > 0) run.mutate(secret)
  }

  return (
    <div className="border-t bg-muted/40 px-4 py-4">
      <form onSubmit={submit} className="space-y-2.5">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{action.title}</h4>
          <p className="mt-1 max-w-2xl text-[13px] text-muted-foreground">{action.description}</p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[18rem] flex-1">
            <Label className="mb-1.5 block" htmlFor={fieldId}>
              {action.inputLabel}
            </Label>
            <Input
              id={fieldId}
              type="password"
              autoComplete="off"
              spellCheck={false}
              className="font-mono text-[13px]"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </div>
          <Button size="sm" type="submit" disabled={run.isPending || value.trim().length === 0}>
            {run.isPending ? "Uruchamiam…" : action.submitLabel}
          </Button>
        </div>

        {run.isError && (
          <Notice tone="critical">
            {run.error instanceof ApiError ? run.error.message : "Nie udało się uruchomić akcji."}
          </Notice>
        )}
        {run.isSuccess && <Notice tone="neutral">{run.data.message}</Notice>}
      </form>
    </div>
  )
}

/**
 * The share answered without touching the network — the earliest warning that
 * a metered source is about to start spending.
 *
 * **Coloured only where spending costs something.** Eighty KRD checks are
 * money and eighty GUS calls are nothing, so the tone follows the cost band the
 * source declared rather than the number. MF counted as the first until
 * 2026-09-25, when its documented daily limit was measured as not enforced.
 */
function localShare(traffic: ProviderTraffic, worstCaseCost: number): DeclaredMetric[] {
  if (traffic.localRatio === null) return []

  const metered = worstCaseCost >= CostBand.METERED
  return [
    {
      key: "local_ratio",
      label: "Lokalnie",
      value: traffic.localRatio,
      format: "ratio",
      tone: metered && traffic.localRatio < 0.8 ? "bad" : "neutral",
      hint: metered
        ? "Udział odpowiedzi bez sieci. Spadek poniżej 80% zapowiada spalanie limitu, zanim ruszy jego licznik."
        : "Udział odpowiedzi bez sieci. To źródło nie ma limitu.",
    },
  ]
}

function trafficFacts(traffic: ProviderTraffic, worstCaseCost: number): DeclaredMetric[] {
  /*
   * Only a metered source has an allowance to draw on, so only its card counts
   * against one. Everyone's `api` calls read as "Limit" once, and MF's kept
   * doing so for a limit production measured as not enforced.
   */
  const spent: DeclaredMetric[] =
    worstCaseCost >= CostBand.METERED
      ? [
          {
            key: "quota_spent",
            label: "Z puli",
            value: traffic.quotaSpent,
            hint: "Ile z nich pobrało sprawdzenie z opłaconej puli źródła.",
          },
        ]
      : []

  return [
    { key: "calls", label: "Wywołania", value: traffic.calls },
    {
      key: "local",
      label: "Lokalnie",
      value: traffic.local,
      hint: "Odpowiedzi z naszej bazy — bez sieci.",
    },
    { key: "cache", label: "Cache", value: traffic.cache },
    { key: "api", label: "API", value: traffic.api, hint: "Wywołania, które wyszły na zewnątrz." },
    {
      key: "none",
      label: "Nie pytano",
      value: traffic.none,
      tone: traffic.none > 0 ? "bad" : "neutral",
      hint: "Decyzje, w których źródła nie zapytano wcale — brak klucza, brak sesji albo odmowa rezerwacji. To nie są odpowiedzi lokalne i nie wchodzą do udziału poniżej.",
    },
    ...spent,
    {
      key: "failures",
      label: "Błędy",
      value: traffic.failures,
      tone: traffic.failures > 0 ? "bad" : "neutral",
      hint: "Błędy i przekroczone czasy. Decyzja odpowiada wtedy bez danych z tego źródła.",
    },
    { key: "slowest_ms", label: "Najwolniej (ms)", value: traffic.slowestMs },
  ]
}

/** The cost band in words: why one source is asked first and another last. */
function costLabel(worstCaseCost: number): string {
  if (worstCaseCost >= CostBand.METERED) return "wydaje limit"
  if (worstCaseCost >= CostBand.UNMETERED) return "bez limitu"
  return "dane lokalne"
}
